import type { InvoiceDto } from "./api/invoices-api";

export interface DashboardChartsData {
  revenueCategories: string[];
  revenueReceived: number[];
  revenueOutstanding: number[];
  incomeCategories: string[];
  incomeSeries: number[];
  incomeTotal: number;
  topSalesLabels: string[];
  topSalesValues: number[];
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function buildDashboardCharts(
  invoices: InvoiceDto[],
  products: Array<{ name: string; sellingPrice: string }>,
  summary: { paidAmount: string | number; totalAmount: string | number },
): DashboardChartsData {
  const now = new Date();
  const revenueCategories: string[] = [];
  const revenueReceived: number[] = [];
  const revenueOutstanding: number[] = [];

  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    revenueCategories.push(d.toLocaleDateString("en-GB", { weekday: "short" }));
    let received = 0;
    let outstanding = 0;
    for (const inv of invoices) {
      if (!inv.createdAt.startsWith(key)) continue;
      const total = Number.parseFloat(inv.total) || 0;
      const paid = Number.parseFloat(inv.paidAmount) || 0;
      received += paid;
      outstanding += Math.max(0, total - paid);
    }
    revenueReceived.push(Math.round(received));
    revenueOutstanding.push(Math.round(outstanding));
  }

  const incomeSeries = new Array(12).fill(0);
  for (const inv of invoices) {
    const d = new Date(inv.createdAt);
    if (d.getFullYear() !== now.getFullYear()) continue;
    incomeSeries[d.getMonth()] += Number.parseFloat(inv.paidAmount) || 0;
  }

  const topProducts = [...products]
    .sort(
      (a, b) =>
        (Number.parseFloat(b.sellingPrice) || 0) - (Number.parseFloat(a.sellingPrice) || 0),
    )
    .slice(0, 3);

  const topSalesLabels = topProducts.length
    ? topProducts.map((p) => p.name)
    : ["Products", "Services", "Other"];
  const topSalesValues = topProducts.length
    ? topProducts.map((p) => Math.round(Number.parseFloat(p.sellingPrice) || 0))
    : [40, 35, 25];

  return {
    revenueCategories,
    revenueReceived,
    revenueOutstanding,
    incomeCategories: MONTHS,
    incomeSeries: incomeSeries.map((v) => Math.round(v)),
    incomeTotal: Number.parseFloat(String(summary.paidAmount)) || 0,
    topSalesLabels,
    topSalesValues,
  };
}

export const billingDashboardCharts: DashboardChartsData = {
  revenueCategories: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
  revenueReceived: [0, 0, 0, 0, 0, 0, 0],
  revenueOutstanding: [0, 0, 0, 0, 0, 0, 0],
  incomeCategories: MONTHS,
  incomeSeries: new Array(12).fill(0),
  incomeTotal: 0,
  topSalesLabels: ["—", "—", "—"],
  topSalesValues: [1, 1, 1],
};
